import { transparentize } from 'polished';
import styled from 'styled-components';

interface ContainerProps {
  $isFocused: boolean;
  $isFilled: boolean;
  $hasToggleButton?: boolean;
  $readOnly?: boolean;
  $multiline?: boolean;
}

export const FeedPostContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const FeedContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const FilterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  > div {
    width: 100%;
    display: flex;
    align-items: center;
  }
`;

export const FeedWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.6rem;
  padding: 0 1.6rem;
`;

export const FilterWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
`;

export const SearchWrapper = styled.div<{ $inputKeyword: boolean }>`
  display: ${props => (props.$inputKeyword ? 'flex' : 'none')};

  > .primary-button {
    /* height: 3.55rem; */
    margin-left: 0.6rem;
    padding: 0 2rem;
    display: flex;
    align-items: center;
    justify-items: center;
    font-family: 'RedHatDisplay-Bold';
    font-size: 1rem;
    border-radius: 0.6rem;
    box-shadow: 0px 3px 15px rgba(255, 152, 0, 0.3);

    > svg {
      margin-right: 0.6rem;
    }
  }
`;

export const SearchInput = styled.div<ContainerProps>`
  display: flex;
  align-items: center;
  border: 0.5px solid
    ${props => (props.$isFocused && !props.$readOnly ? '#FF9800' : '#e2e2e2')};
  background: ${props =>
    props.$readOnly ? transparentize(0.7, '#e2e2e2') : '#fff'};
  font-size: 1rem;
  padding: 0 0.8rem;
  padding-right: ${props => (props.$multiline ? 0 : '1.167rem')};
  border-radius: 0.56rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  width: 100%;
  height: ${props => (props.$multiline ? '9rem' : '3.05rem')};
  cursor: ${props => (props.$readOnly ? 'not-allowed' : 'unset')};

  input {
    font-size: 1rem;
    background: transparent;
    flex: 1;
    border: 0;
    color: #323232;
    vertical-align: middle;
    cursor: ${props => (props.$readOnly ? 'not-allowed' : 'unset')};

    &::placeholder {
      font-size: 1rem;
      vertical-align: middle;
    }
  }

  > svg:first-child {
    margin-right: 0.88rem;
    stroke: ${props => (props.$isFocused ? '#FF9800' : '#323232')};
    opacity: 1 !important;
    visibility: visible !important;
  }
`;
export const DropdownContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0px 0.5em 0px 1.8em;
  position: relative;
  min-width: 12rem;
  padding: 0.72rem 1rem;
  background-color: #ffffff;
  border-radius: 0.8rem;
  border: 1px solid #e0e0e0;

  > button {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    color: #323232;
    opacity: 0.8;

    > svg {
      margin-left: 0.5rem;
    }

    &:hover {
      color: #323232 !important;
      opacity: 1;
    }
  }

  // The 'nav' item corresponds to Dropdown Component
  > nav {
    width: 100%;
    z-index: 1500;
    background: #ffffff;
    border-radius: 0.8rem;
    position: absolute;
    top: 3.5rem;
    left: 0;
    box-shadow: 0px 10px 30px rgba(85, 85, 85, 0.16);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-20px);
    transition:
      opacity 0.3s ease,
      transform 0.3s ease,
      visibility 0.3s;

    &.visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }

    &.hidden {
      display: none;
    }

    > ul {
      width: 100%;
      padding: 0;
      margin: 0;

      > li {
        width: 100%;
        white-space: nowrap;
        font-size: 1rem;
        transition: background 0.3s;
        border-bottom: 1px solid #e0e0e0;

        &:first-child {
          border-top-left-radius: 0.8rem;
          border-top-right-radius: 0.8rem;
        }

        &:last-child {
          border-bottom-left-radius: 0.8rem;
          border-bottom-right-radius: 0.8rem;
        }

        &:last-child:hover {
          border-bottom-left-radius: 0.8rem;
          border-bottom-right-radius: 0.8rem;
          background: rgba(255, 46, 62, 0.05);
        }

        &:not(:last-child):hover {
          background: rgba(50, 54, 80, 0.05);
        }

        > button {
          width: 100%;
          height: 2.6rem;
          display: flex;
          align-items: center;
          justify-content: center; /* Center the text horizontally */
          padding: 0 0.8rem;

          > svg {
            margin-right: 1rem;
            visibility: visible;
          }
        }
      }
    }
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  margin-bottom: 1rem;
  gap: 1rem;
`;

export const PaginationButton = styled.button`
  background: transparent;
  border: 1px solid #e2e2e2;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const PageInfo = styled.span`
  font-size: 0.9rem;
  color: #323232;
`;
